import { Link } from "react-router-dom";
import type { Article } from "../../types";

const CAT_COLOR: Record<string, string> = {
  恋情: "bg-primary-100 text-primary-700",
  番位: "bg-accent-100 text-accent-900",
  争议: "bg-secondary-100 text-secondary-900",
  综艺: "bg-primary-50 text-primary-700",
  影视: "bg-accent-100 text-accent-800",
  造型: "bg-secondary-100 text-secondary-800",
  合约: "bg-primary-100 text-primary-700",
  考古: "bg-accent-50 text-accent-800",
  热播剧: "bg-secondary-100 text-secondary-800",
};

export function CategoryTag({ label }: { label: string }) {
  const cls = CAT_COLOR[label] || "bg-secondary-100 text-secondary-800";
  return <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${cls} whitespace-nowrap`}>{label}</span>;
}

export function SourceBadge({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs text-foreground-400 whitespace-nowrap">
      <i className="ri-community-line" aria-hidden="true"></i>
      {name}
    </span>
  );
}

export function Stats({ views, likes, comments }: { views: string; likes: number; comments: number }) {
  return (
    <div className="flex items-center gap-3 text-xs text-foreground-400 whitespace-nowrap">
      <span className="inline-flex items-center gap-1"><i className="ri-eye-line" aria-hidden="true"></i>{views}</span>
      <span className="inline-flex items-center gap-1"><i className="ri-heart-3-line" aria-hidden="true"></i>{(likes / 1000).toFixed(1)}k</span>
      <span className="inline-flex items-center gap-1"><i className="ri-chat-3-line" aria-hidden="true"></i>{comments}</span>
    </div>
  );
}

export function RowCard({ article, rank }: { article: Article; rank?: number }) {
  return (
    <article className="group flex gap-4 rounded-xl bg-background-100/80 p-3 hover:bg-background-100 transition">
      <Link to={`/article/${article.id}`} className="block shrink-0">
        <div className="relative w-28 sm:w-36 aspect-[3/2] rounded-lg overflow-hidden bg-background-200">
          <img src={article.cover} alt={article.title} title={`${article.title} 明星爆料`} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-500" loading="lazy" />
        </div>
      </Link>
      <div className="flex flex-col justify-between min-w-0 flex-1 py-0.5">
        <div>
          {rank !== undefined && (
            <span className={`inline-flex items-center justify-center w-5 h-5 mr-1.5 text-xs font-bold rounded ${rank <= 3 ? "bg-primary-600 text-background-50" : "bg-background-200 text-foreground-600"}`}>{rank}</span>
          )}
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <CategoryTag label={article.category} />
            <SourceBadge name={article.sourceName} />
            <span className="text-xs text-foreground-400 whitespace-nowrap">{article.time}</span>
          </div>
          <Link to={`/article/${article.id}`}>
            <h4 className="font-heading font-bold text-foreground-900 leading-snug text-sm sm:text-base group-hover:text-primary-700 line-clamp-2 transition">{article.title}</h4>
          </Link>
        </div>
        <p className="hidden sm:block text-xs text-foreground-500 mt-2 line-clamp-1">{article.summary}</p>
        <div className="flex items-center justify-between mt-2.5">
          <Stats views={article.views} likes={article.likes} comments={article.comments} />
          <Link to={`/article/${article.id}`} className="text-xs text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-1 whitespace-nowrap">
            吃透瓜 <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
      </div>
    </article>
  );
}

export function StackCard({ article }: { article: Article }) {
  return (
    <article className="group overflow-hidden rounded-xl bg-background-100 hover:bg-background-100 transition">
      <Link to={`/article/${article.id}`} className="block">
        <div className="relative aspect-[16/10] overflow-hidden bg-background-200">
          <img src={article.cover} alt={article.title} title={`${article.title} 明星爆料`} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-500" loading="lazy" />
          {article.hot && (
            <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-md bg-primary-600 text-background-50 px-2 py-0.5 text-xs font-medium">
              <i className="ri-fire-fill text-sm" aria-hidden="true"></i> 热
            </span>
          )}
        </div>
      </Link>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <CategoryTag label={article.category} />
          <SourceBadge name={article.sourceName} />
          <span className="text-xs text-foreground-400">{article.time}</span>
        </div>
        <Link to={`/article/${article.id}`}>
          <h4 className="font-heading font-bold text-foreground-900 leading-snug text-sm md:text-base line-clamp-2 group-hover:text-primary-700 transition">{article.title}</h4>
        </Link>
        <p className="text-xs text-foreground-500 mt-2 line-clamp-2">{article.summary}</p>
        <div className="flex items-center justify-between mt-3.5">
          <Stats views={article.views} likes={article.likes} comments={article.comments} />
        </div>
      </div>
    </article>
  );
}